import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, ReceiptText, Users2, Building2, ScrollText, Settings as SettingsIcon,
  LogOut, Menu, X, Moon, Sun, Inbox,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/vouchers", label: "Vouchers", icon: ReceiptText },
  { to: "/cc", label: "CC", icon: Inbox },
  { to: "/vendors", label: "Vendors", icon: Building2 },
  { to: "/audit", label: "Audit Log", icon: ScrollText, admin: true },
  { to: "/users", label: "Users", icon: Users2, admin: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, admin: true },
];

export function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState("Dubai Payment Voucher");
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    api.get("/settings").then(({ data }) => data?.company_name && setCompany(data.company_name)).catch(() => {});
  }, []);

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const links = NAV.filter((n) => !n.admin || isAdmin);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex">
      {/* Sidebar */}
      <aside
        className={cn(
          "no-print fixed lg:sticky lg:top-0 lg:h-screen z-50 inset-y-0 left-0 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col transition-transform duration-200 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="px-5 py-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-center">
          <img src="/logo.png" alt="Al Arabia — Arabian Contracting Services" className="h-16 w-auto object-contain" data-testid="app-logo" />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {links.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                )
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 leading-tight">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={doLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </Button>
        </div>
      </aside>

      {open && <div className="no-print fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="no-print sticky top-0 z-30 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-b border-slate-200 dark:border-slate-800 px-4 md:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-display font-semibold text-base md:text-lg truncate">{company}</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-[11px] font-mono px-2 py-1 rounded-md bg-accent text-accent-foreground border border-primary/20">
              AED · 5% VAT
            </span>
            <Button variant="ghost" size="icon" onClick={toggleDark} data-testid="theme-toggle">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">{children}</main>
      </div>
    </div>
  );
}
